const defaultApiUrl = process.env.NODE_ENV === 'production' ? 'http://backend:8000/api' : 'http://127.0.0.1:8000/api';
const API_URL = process.env.API_URL || defaultApiUrl; // Server-side default

const getBaseUrl = () => {
    if (typeof window === 'undefined') return API_URL;
    return '/api'; // Client-side: relative URL, always same-origin (covered by CSP 'self')
};

export async function fetchAPI(endpoint: string, options: RequestInit = {}) {
    const url = `${getBaseUrl()}${endpoint}`;

    // Auto-inject auth token if not provided in options
    let autoAuthHeaders: Record<string, string> = {};
    const inputHeaders = (options.headers as Record<string, string>) || {};
    if (!inputHeaders['Authorization'] && !inputHeaders['authorization']) {
        try {
            const { getAuthHeaders } = await import('./auth-helper');
            autoAuthHeaders = await getAuthHeaders() as Record<string, string>;
        } catch { /* not in auth context or fetch failed */ }
    }

    const headers: Record<string, string> = {
        ...autoAuthHeaders,
        ...inputHeaders,
    };

    // The admin panel must always see canonical (base-language) data, never whatever locale the
    // same browser last picked while browsing the storefront as a customer — stag_locale/
    // stag_currency are storefront-only cookies, but fetchAPI is shared, so without this check an
    // admin viewing /admin right after switching the storefront to e.g. NL would silently get back
    // NL-translated category/product names instead of the canonical values + a locale switcher.
    let isAdminRoute = false;
    if (typeof window !== 'undefined') {
        isAdminRoute = window.location.pathname.startsWith('/admin');
    } else {
        try {
            const { headers: nextHeaders } = await import('next/headers');
            const h = await nextHeaders();
            // Set by proxy.ts on every request (frontend/proxy.ts).
            isAdminRoute = (h.get('x-pathname') || '').startsWith('/admin');
        } catch { /* not in request context */ }
    }
    // A handful of admin tools (e.g. agency-content's translation preview) deliberately ask for a
    // specific locale via an explicit `?locale=` query param — that intent must win over the
    // admin-canonical default below.
    const hasExplicitLocaleParam = /[?&]locale=/.test(endpoint);

    if (isAdminRoute && !hasExplicitLocaleParam && !headers['X-Localize'] && !headers['x-localize']) {
        headers['X-Localize'] = 'false';
    } else {
        // Auto-inject locale and currency from cookies
        let localeCookie: string | undefined;
        let currencyCookie: string | undefined;

        if (typeof window === 'undefined') {
            try {
                const { cookies } = await import('next/headers');
                const cookieStore = await cookies();
                localeCookie = cookieStore.get('stag_locale')?.value;
                currencyCookie = cookieStore.get('stag_currency')?.value;
            } catch { /* not in request context or server side next/headers error */ }
        } else {
            const getCookie = (name: string) => {
                const value = `; ${document.cookie}`;
                const parts = value.split(`; ${name}=`);
                if (parts.length === 2) return parts.pop()?.split(';').shift();
                return undefined;
            };
            // The page's own language/currency (set on <html> by app/layout.tsx from what proxy.ts
            // resolved for this URL) — a cookie can still hold an older choice, e.g. on another
            // market's address.
            localeCookie = document.documentElement.lang || getCookie('stag_locale');
            currencyCookie = document.documentElement.dataset.currency || getCookie('stag_currency');
        }

        if (localeCookie) {
            // X-Locale is a custom header browsers cannot override (unlike Accept-Language)
            headers['X-Locale'] = localeCookie;
            if (!headers['Accept-Language']) {
                headers['Accept-Language'] = localeCookie;
            }
        }
        if (currencyCookie && !headers['X-Currency']) {
            headers['X-Currency'] = currencyCookie;
        }
    }


    // Only set Content-Type to application/json if not FormData
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    try {
        const res = await fetch(url, {
            ...options,
            headers,
        });

        if (!res.ok) {
            let errorData: any = {};
            const rawBody = await res.text().catch(() => "");
            
            try {
                if (rawBody) {
                    errorData = JSON.parse(rawBody);
                }
            } catch {
                console.warn(`Could not parse error response as JSON: ${rawBody}`);
            }

            console.error(`API Error ${res.status} at ${url}`, errorData);
            
            if (res.status === 401 && typeof window !== 'undefined') {
                const [{ signOut }, { invalidateClientSessionCache }] = await Promise.all([
                    import('next-auth/react'),
                    import('./auth-helper'),
                ]);
                invalidateClientSessionCache();
                // If on storefront, silently clear expired session cookie without redirecting user to login page
                if (!window.location.pathname.startsWith('/admin')) {
                    signOut({ redirect: false });
                }
                // In admin, do NOT force redirect to /logowanie — this destroys unsaved form state.
                // SessionExpiredModal in session-provider.tsx allows in-place re-authentication.
            }
            
            // Throw error so callers can catch it and show specific messages
            // `detail` can be a structured object (e.g. { message, suggested_date }) instead of a
            // plain string — callers needing the extra fields read them off `error.data.detail`.
            // It can also be FastAPI's own 422 shape — a list of { loc, msg } Pydantic validation
            // errors — which has neither `.message` nor is itself a string, so without this it
            // silently fell through to the generic "Błąd serwera (422)" instead of e.g. "value is
            // not a valid email address".
            let detailMsg: string | undefined;
            if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
                detailMsg = errorData.detail.map((e: any) => {
                    const field = Array.isArray(e?.loc) ? e.loc.filter((p: any) => p !== 'body').join('.') : null;
                    return field ? `${field}: ${e?.msg}` : e?.msg;
                }).filter(Boolean).join('; ');
            } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
                detailMsg = errorData.detail.message;
            } else {
                detailMsg = errorData.detail;
            }
            const errorMsg = detailMsg || errorData.message || (typeof errorData === 'string' ? errorData : null) || `Błąd serwera (${res.status})`;
            const error = new Error(errorMsg);
            (error as any).status = res.status;
            (error as any).data = errorData;
            throw error;
        }

        if (res.status === 204) {
            return {};
        }

        return res.json();
    } catch (error) {
        // AbortController is used by live checkout quotes to cancel the previous destination
        // request while the customer is still typing. That is expected control flow, not an API
        // failure, so keep the console useful by not reporting it as an error.
        if (!(error instanceof Error) || error.name !== 'AbortError') {
            console.error(`Fetch error at ${url}:`, error);
        }
        throw error; // Throw so callers can handle the network error
    }
}
